// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/cofre/FaturamentoPainel.tsx
//  Faturamento de um contribuinte a partir do Cofre Fiscal: projeção do ano no Simples
//  (sublimite e limite), 24 meses por tipo de receita, composição, UF, tipo de cliente,
//  maiores clientes e produtos, e a simulação Simples × Lucro Presumido. Usado no Portal
//  do Contador e na Central Fiscal do ERP — quem usa passa as funções que buscam os dados.
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Calculator, Info, Loader2, TrendingDown, TrendingUp } from 'lucide-react';
import {
  Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatCurrency } from '@/lib/utils';
import { ANEXOS_SIMPLES } from './analiseFiscal';
import {
  COR_SITUACAO_SIMPLES, ehRegimeNormal, percentual, ROTULO_SITUACAO_SIMPLES, rotuloMesCurto, variacao,
  type ComparacaoRegime, type Fatia, type Faturamento, type ParametrosRegime, type ProjecaoSimples,
} from './faturamento';

const SUGERIDO = '__sugerido__';
const COR = { producao: '#2563eb', revenda: '#0ea5e9', servico: '#8b5cf6', liquida: '#16a34a' };

interface Props {
  /** Chave de cache (inclua quem é o contribuinte). */
  chave: unknown[];
  competencia: number;
  carregar: (competencia: number) => Promise<Faturamento>;
  simular: (competencia: number, p: ParametrosRegime) => Promise<ComparacaoRegime>;
}

const formatarDoc = (d: string) =>
  d.length === 14 ? d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')
    : d.length === 11 ? d.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4') : d;
const milhar = (v: number) => (Math.abs(v) >= 1_000_000 ? `${(v / 1_000_000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mi`
  : Math.abs(v) >= 1_000 ? `${(v / 1_000).toLocaleString('pt-BR', { maximumFractionDigits: 0 })} mil` : String(Math.round(v)));

export function FaturamentoPainel({ chave, competencia, carregar, simular }: Props) {
  const { data, isLoading, error } = useQuery({
    queryKey: [...chave, 'faturamento', competencia],
    queryFn: () => carregar(competencia),
    retry: false,
  });

  if (isLoading) return <div className="space-y-4"><Skeleton className="h-40" /><Skeleton className="h-72" /></div>;
  if (error || !data)
    return (
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>{error instanceof Error ? error.message : 'Faturamento indisponível.'}</AlertDescription>
      </Alert>
    );

  const var12 = variacao(data.receita12, data.receita12Anterior);
  const mes = data.meses[data.meses.length - 1];
  const anterior = data.meses[data.meses.length - 2];
  // Mês aberto contra um fechado daria sempre queda: só compara meses fechados.
  const hoje = new Date();
  const emAndamento = competencia === hoje.getFullYear() * 100 + hoje.getMonth() + 1;
  const varMes = mes && anterior && !emAndamento ? variacao(mes.liquida, anterior.liquida) : null;

  return (
    <div className="space-y-6">
      {data.observacao && (
        <Alert><Info className="h-4 w-4" /><AlertDescription>{data.observacao}</AlertDescription></Alert>
      )}

      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <Kpi titulo={emAndamento ? 'Receita líquida do mês (em andamento)' : 'Receita líquida do mês'}
          valor={formatCurrency(mes?.liquida ?? 0)} variacao={varMes} legenda="sobre o mês anterior" />
        <Kpi titulo="Últimos 12 meses" valor={formatCurrency(data.receita12)} variacao={var12} legenda="sobre os 12 anteriores" />
        <Kpi titulo="Notas de venda no mês" valor={(mes?.documentos ?? 0).toLocaleString('pt-BR')} />
        <Kpi titulo="Ticket médio no mês" valor={formatCurrency(mes?.ticketMedio ?? 0)} />
      </div>

      {ehRegimeNormal(data.crt) ? (
        <Alert><Info className="h-4 w-4" /><AlertDescription>
          As notas saem com CRT 3 (regime normal): a empresa não está no Simples Nacional, e a projeção de limite não se aplica.
        </AlertDescription></Alert>
      ) : (
        <ProjecaoCard p={data.projecao} />
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Receita por mês</CardTitle>
          <CardDescription>Últimos 24 meses, pelo CFOP das notas autorizadas; a linha é a receita líquida das devoluções.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data.meses.map((m) => ({ ...m, mes: rotuloMesCurto(m.competencia) }))}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="mes" fontSize={11} interval="preserveStartEnd" />
                <YAxis fontSize={11} tickFormatter={milhar} width={56} />
                <Tooltip formatter={(v: number) => formatCurrency(v)} />
                <Legend />
                <Bar dataKey="producao" name="Produção própria" stackId="r" fill={COR.producao} />
                <Bar dataKey="revenda" name="Revenda" stackId="r" fill={COR.revenda} />
                <Bar dataKey="servico" name="Serviço" stackId="r" fill={COR.servico} />
                <Line dataKey="liquida" name="Líquida" stroke={COR.liquida} strokeWidth={2} dot={false} type="monotone" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-3">
        <Fatias titulo="O que a empresa vende" descricao="Últimos 12 meses" fatias={data.composicao} />
        <Fatias titulo="Para quem vende" descricao="A parte vendida a empresas pesa no crédito de IBS/CBS a partir de 2027"
          fatias={data.porTipoCliente} />
        <Fatias titulo="Para onde vende" descricao="Receita por UF do destinatário" fatias={data.porUf} />
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Maiores clientes</CardTitle>
            <CardDescription>Últimos 12 meses; concentração alta é risco para o negócio do cliente.</CardDescription></CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            {data.clientes.length === 0 ? <Vazio /> : (
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Cliente</TableHead><TableHead className="text-right">Notas</TableHead>
                  <TableHead className="text-right">Valor</TableHead><TableHead className="text-right">%</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {data.clientes.map((c) => (
                    <TableRow key={c.cnpjCpf}>
                      <TableCell className="max-w-64">
                        <span className="block truncate">{c.nome ?? '—'}</span>
                        <span className="block text-xs text-muted-foreground">{formatarDoc(c.cnpjCpf)}</span>
                      </TableCell>
                      <TableCell className="text-right">{c.documentos.toLocaleString('pt-BR')}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">{formatCurrency(c.valor)}</TableCell>
                      <TableCell className="text-right">{percentual(c.percentual)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Produtos mais vendidos</CardTitle>
            <CardDescription>Últimos 12 meses, pelo código do produto na nota.</CardDescription></CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            {data.produtos.length === 0 ? <Vazio /> : (
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Produto</TableHead><TableHead>NCM</TableHead>
                  <TableHead className="text-right">Valor</TableHead><TableHead className="text-right">%</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {data.produtos.map((p, i) => (
                    <TableRow key={`${p.codigo}-${i}`}>
                      <TableCell className="max-w-64">
                        <span className="block truncate">{p.descricao ?? '—'}</span>
                        <span className="block text-xs text-muted-foreground">
                          Cód. {p.codigo ?? '—'} · {p.quantidade.toLocaleString('pt-BR', { maximumFractionDigits: 3 })} un.
                        </span>
                      </TableCell>
                      <TableCell className="font-mono">{p.ncm ?? '—'}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">{formatCurrency(p.valor)}</TableCell>
                      <TableCell className="text-right">{percentual(p.percentual)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {!ehRegimeNormal(data.crt) && <SimulacaoRegimes chave={chave} competencia={competencia} simular={simular} />}
    </div>
  );
}

// ── Projeção do Simples ──────────────────────────────────────────────────────

function ProjecaoCard({ p }: { p: ProjecaoSimples }) {
  return (
    <Card className={p.situacao === 'ok' ? undefined : 'border-amber-500'}>
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row sm:items-start gap-2">
          <div>
            <CardTitle className="text-base">Simples Nacional em {p.ano}</CardTitle>
            <CardDescription>O desenquadramento olha a receita acumulada no ano, não o RBT12.</CardDescription>
          </div>
          <Badge variant="outline" className={`sm:ml-auto ${COR_SITUACAO_SIMPLES[p.situacao]}`}>{ROTULO_SITUACAO_SIMPLES[p.situacao]}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {p.mensagem && (
          <Alert variant={p.situacao === 'atencao' ? 'default' : 'destructive'}>
            <AlertTriangle className="h-4 w-4" /><AlertDescription>{p.mensagem}</AlertDescription>
          </Alert>
        )}
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-3">
          <Kpi titulo={`Receita de ${p.ano} até agora`} valor={formatCurrency(p.receitaAno)} />
          <Kpi titulo={`Média dos últimos ${p.mesesNaMedia || 0} meses fechados`} valor={formatCurrency(p.mediaMensal)} />
          <Kpi titulo="Projeção até dezembro" valor={formatCurrency(p.projecaoAno)} destaque />
        </div>
        <BarraSimples p={p} />
        <p className="text-xs text-muted-foreground">
          Projeção pela média recente, sem sazonalidade. Empresa aberta neste ano tem limite proporcional aos meses de atividade, que a projeção não considera.
        </p>
      </CardContent>
    </Card>
  );
}

/** Receita do ano (cheia) e projeção (clara) contra os marcos de 3,6 mi, 4,8 mi e o excesso de 20%. */
export function BarraSimples({ p, compacta = false }: { p: ProjecaoSimples; compacta?: boolean }) {
  const escala = Math.max(5_760_000, p.projecaoAno * 1.05);
  const pos = (v: number) => `${Math.min(100, (v / escala) * 100)}%`;
  const marcos = compacta ? p.marcos.filter((m) => m.chave === 'sublimite' || m.chave === 'limite') : p.marcos;
  return (
    <div className={compacta ? 'pt-1' : 'pt-4'}>
      <div className="relative h-3 rounded-full bg-muted overflow-hidden">
        <div className="absolute inset-y-0 left-0 bg-primary/25" style={{ width: pos(p.projecaoAno) }} />
        <div className="absolute inset-y-0 left-0 bg-primary" style={{ width: pos(p.receitaAno) }} />
        {marcos.map((m) => (
          <div key={m.chave} className="absolute inset-y-0 w-0.5 bg-foreground/60" style={{ left: pos(m.valor) }} title={m.rotulo} />
        ))}
      </div>
      {!compacta && (
        <div className="relative h-5 mt-1 text-[11px] text-muted-foreground">
          {marcos.filter((m) => m.chave === 'sublimite' || m.chave === 'limite').map((m) => (
            <span key={m.chave} className="absolute -translate-x-1/2 whitespace-nowrap" style={{ left: pos(m.valor) }}>
              {milhar(m.valor)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Simples × Lucro Presumido ────────────────────────────────────────────────

function SimulacaoRegimes({ chave, competencia, simular }: Pick<Props, 'chave' | 'competencia' | 'simular'>) {
  const [anexo, setAnexo] = useState<number | undefined>();
  const [icmsIss, setIcmsIss] = useState('');
  const [folha, setFolha] = useState('');
  const [parametros, setParametros] = useState<ParametrosRegime | null>(null);
  const numero = (s: string) => Number(s.replace(/\./g, '').replace(',', '.')) || 0;

  const { data, isFetching, error } = useQuery({
    queryKey: [...chave, 'regimes', competencia, parametros],
    queryFn: () => simular(competencia, parametros!),
    enabled: parametros !== null,
    retry: false,
  });

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2"><Calculator className="h-4 w-4" />Simples × Lucro Presumido</CardTitle>
        <CardDescription>
          Quanto a empresa pagaria em cada regime nos últimos 12 meses. Informe a carga de ICMS/ISS fora do Simples e a folha para a comparação ficar justa.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 items-end"
          onSubmit={(e) => { e.preventDefault(); setParametros({ anexo, icmsIss: numero(icmsIss), folha: numero(folha) }); }}>
          <div className="space-y-1.5">
            <Label>Anexo do Simples</Label>
            <Select value={anexo ? String(anexo) : SUGERIDO} onValueChange={(v) => setAnexo(v === SUGERIDO ? undefined : Number(v))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={SUGERIDO}>O sugerido pelas notas</SelectItem>
                {Object.entries(ANEXOS_SIMPLES).map(([n, nome]) => <SelectItem key={n} value={n}>{nome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sim-icms">ICMS/ISS fora do Simples (% da receita)</Label>
            <Input id="sim-icms" inputMode="decimal" placeholder="ex.: 4,5" value={icmsIss} onChange={(e) => setIcmsIss(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sim-folha">Folha dos 12 meses (R$)</Label>
            <Input id="sim-folha" inputMode="decimal" placeholder="ex.: 240.000" value={folha} onChange={(e) => setFolha(e.target.value)} />
          </div>
          <Button type="submit" disabled={isFetching}>
            {isFetching && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Simular
          </Button>
        </form>

        {error && <Alert><Info className="h-4 w-4" /><AlertDescription>{(error as Error).message}</AlertDescription></Alert>}
        {data && (
          <div className="space-y-3">
            <Alert variant={data.diferenca < 0 ? 'destructive' : 'default'}>
              {data.diferenca < 0 ? <TrendingDown className="h-4 w-4" /> : <TrendingUp className="h-4 w-4" />}
              <AlertDescription>{data.conclusao}</AlertDescription>
            </Alert>
            <div className="grid gap-4 md:grid-cols-2">
              <Regime titulo={`Simples Nacional · ${data.nomeAnexo}`} total={data.totalSimples} linhas={[
                [`DAS estimado (média de ${percentual(data.aliquotaMediaSimples)})`, data.das],
                ...(data.encargosForaDoDas ? [['Encargos da folha fora do DAS (anexo IV)', data.encargosForaDoDas] as [string, number]] : []),
              ]} />
              <Regime titulo="Lucro Presumido" total={data.totalPresumido} linhas={[
                ['IRPJ', data.irpj], ['CSLL', data.csll], ['PIS', data.pis], ['COFINS', data.cofins],
                ['ICMS/ISS informado', data.icmsIss], ['Encargos sobre a folha', data.encargosFolha],
              ]} />
            </div>
            <ul className="text-xs text-muted-foreground list-disc pl-4 space-y-0.5">
              <li>Receita considerada: {formatCurrency(data.receitaComercio)} de mercadoria e {formatCurrency(data.receitaServico)} de serviço.</li>
              {data.premissas.map((p) => <li key={p}>{p}</li>)}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Regime({ titulo, total, linhas }: { titulo: string; total: number; linhas: [string, number][] }) {
  return (
    <div className="rounded-md border">
      <p className="px-4 py-2 text-sm font-medium border-b">{titulo}</p>
      <Table>
        <TableBody>
          {linhas.map(([rotulo, valor]) => (
            <TableRow key={rotulo}><TableCell>{rotulo}</TableCell><TableCell className="text-right whitespace-nowrap">{formatCurrency(valor)}</TableCell></TableRow>
          ))}
          <TableRow className="font-semibold"><TableCell>Total no ano</TableCell><TableCell className="text-right whitespace-nowrap">{formatCurrency(total)}</TableCell></TableRow>
        </TableBody>
      </Table>
    </div>
  );
}

// ── Auxiliares ───────────────────────────────────────────────────────────────

function Kpi({ titulo, valor, variacao: v, legenda, destaque = false }: {
  titulo: string; valor: string; variacao?: number | null; legenda?: string; destaque?: boolean;
}) {
  return (
    <div className={`rounded-md border p-3 ${destaque ? 'border-primary' : ''}`}>
      <p className="text-base sm:text-lg font-semibold break-words">{valor}</p>
      <p className="text-xs text-muted-foreground">{titulo}</p>
      {v !== undefined && v !== null && (
        <p className={`text-xs mt-1 ${v >= 0 ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>
          {v >= 0 ? '+' : ''}{percentual(v)} {legenda}
        </p>
      )}
    </div>
  );
}

function Fatias({ titulo, descricao, fatias }: { titulo: string; descricao: string; fatias: Fatia[] }) {
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">{titulo}</CardTitle><CardDescription>{descricao}</CardDescription></CardHeader>
      <CardContent className="space-y-3">
        {fatias.length === 0 ? <p className="text-sm text-muted-foreground">Sem vendas no período.</p> : fatias.map((f) => (
          <div key={f.chave} className="space-y-1">
            <div className="flex justify-between gap-2 text-sm">
              <span className="truncate">{f.rotulo}</span>
              <span className="text-muted-foreground whitespace-nowrap">{percentual(f.percentual)}</span>
            </div>
            <div className="h-2 rounded-full bg-muted overflow-hidden">
              <div className="h-full bg-primary" style={{ width: `${Math.min(100, Math.max(0, f.percentual))}%` }} />
            </div>
            <p className="text-xs text-muted-foreground">{formatCurrency(f.valor)}</p>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

const Vazio = () => <p className="px-6 pb-6 text-sm text-muted-foreground">Sem vendas no período.</p>;
